import React from "react";
import { Container, Divider, Box, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import { ruRU, enUS } from "@mui/x-date-pickers/locales";
import TaskHeader from "../components/TaskHeader";
import TaskForm from "../components/TaskForm";
import TaskView from "../components/TaskView";
import SubtasksAccordion from "../components/Accordions/SubtasksAccordion";
import AttachmentsAccordion from "../components/Accordions/AttachmentsAccordion";
import { useProjectTaskDetail } from "../hooks/useProjectTaskDetail";
import Notification from "../components/Common/Notification";
import DraftPromptDialog from "../components/Common/DraftPromptDialog";
import { updateProjectTask } from "../services/projectService";
import { normalizeProjectColumnColor } from "../utils/statusColors";

const ProjectTaskDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    task,
    project,
    projectTask,
    isEditing,
    setIsEditing,
    form,
    users,
    mappedSubtasks,
    anchorEl,
    handleTextChange,
    handleSelectChange,
    handleDateChange,
    handleSave,
    handleDelete,
    handleCancel,
    handleOpenMenu,
    handleCloseMenu,
    handleRefresh,
    getStatusConfig,
    id,
    error,
    setError,
    success,
    setSuccess,
    isLoading,
    setNotification,
    draftPromptOpen,
    handleUseDraft,
    handleStartNew,
  } = useProjectTaskDetail();
  const { i18n } = useTranslation();
  const locale = i18n.language === "en_US" ? enUS : ruRU;

  const projectColumns: Array<[string, string, string]> = Array.isArray(
    project?.columns,
  )
    ? project?.columns ?? []
    : [];
  const columnOptions = projectColumns.map(
    (column: [string, string, string]) => ({
      id: column?.[0],
      name: column?.[1],
      color: normalizeProjectColumnColor(column?.[2], column?.[1]),
    }),
  );
  const columnNames = columnOptions
    .map((column: { name?: string }) => column.name)
    .filter((name: string | undefined): name is string => Boolean(name));
  const columnColors = columnOptions.reduce(
    (acc: Record<string, string>, column: { name?: string; color?: string }) => {
    if (column.name) {
      acc[column.name] = column.color || "#757575";
    }
    return acc;
  }, {} as Record<string, string>);
  const currentColumnName =
    projectTask?.columnName || projectTask?.status || task?.status || "";

  const handleCloseNotification = () => {
    setError(null);
    setSuccess(null);
  };

  if (isLoading || (!task && id && id !== "new")) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language === "en_US" ? "en" : "ru"}
      localeText={
        locale.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <Container sx={{ px: { xs: 0, sm: 2 } }}>
        <TaskHeader
          task={task}
          isEditing={isEditing}
          anchorEl={anchorEl}
          handleOpenMenu={handleOpenMenu}
          handleCloseMenu={handleCloseMenu}
          setIsEditing={setIsEditing}
          handleDelete={handleDelete}
          handleCancel={handleCancel}
          handleSave={handleSave}
          error={error}
          setError={setError}
          success={success}
          setSuccess={setSuccess}
          isLoading={isLoading}
          backUrl={`/project/${projectId}/tasks`}
        />
        <Divider sx={{ mb: 2 }} />
        {isEditing || id === "new" ? (
          <TaskForm
            form={form}
            users={users}
            isEditing={isEditing || id === "new"}
            handleTextChange={handleTextChange}
            handleSelectChange={handleSelectChange}
            handleDateChange={handleDateChange}
          />
        ) : task ? (
          <TaskView
            task={task}
            onStatusChange={handleRefresh}
            onStatusUpdate={async (newStatus: string, resolution?: string) => {
              const selectedColumn = columnOptions.find(
                (column: { name?: string }) => column.name === newStatus,
              );
              const updateData: any = {
                columnName: newStatus,
                ...(selectedColumn?.id && { columnId: selectedColumn.id }),
              };
              if (newStatus === "Completed") {
                updateData.cSolution = resolution || "";
              }
              await updateProjectTask(task.id, updateData);
              await handleRefresh();
            }}
            onStatusUpdateSuccess={(message) => setSuccess(message)}
            statusTypeOverride="projectTask"
            availableStatusesOverride={columnNames}
            statusColorsOverride={columnColors}
            currentStatusOverride={currentColumnName}
          />
        ) : null}
        <Box sx={{ mt: 4 }}>
          {id && id !== "new" && !form.taskParentId && projectId && (
            <SubtasksAccordion
              parentId={id}
              subtasks={mappedSubtasks}
              onRefresh={handleRefresh}
              getStatusConfig={getStatusConfig}
              customNavigate={(item) => `/project/${projectId}/task/${item.id}`}
            />
          )}
          {id === "new" ? (
            <AttachmentsAccordion
              parentId="new"
              onRefresh={handleRefresh}
              setNotification={setNotification}
              parentType="ProjectTask"
            />
          ) : id && id !== "new" ? (
            <AttachmentsAccordion
              parentId={id}
              onRefresh={handleRefresh}
              setNotification={setNotification}
              parentType="ProjectTask"
            />
          ) : null}
        </Box>
        <Notification
          open={!!error}
          message={error || ""}
          severity="error"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <Notification
          open={!!success}
          message={success || ""}
          severity="success"
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <DraftPromptDialog
          open={draftPromptOpen}
          onUseDraft={handleUseDraft}
          onStartNew={handleStartNew}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default ProjectTaskDetailPage;
